import { ASKS } from './regularizationInsights.js';

/**
 * Regularization "through the vendor" — the rules, with no API or DOM in them.
 *
 * The vendor fills the form, not the admin. The admin only chooses who gets the link and how
 * many: recent vendors, asked to verify the details they gave; legacy vendors, asked to
 * complete the requirements the new form added. A sent link then lives its own life — sent,
 * submitted, verified, or expired — and so does the mail carrying it (delivered, bounced, failed);
 * that is what the screen reports back.
 */

const DAY = 86_400_000;

/** How long after registration a vendor still counts as recent. */
export const RECENT_DAYS = 90;
/** How long a link stays valid once sent. */
export const LINK_VALID_DAYS = 7;

export const SEGMENTS = [
  {
    key: 'recent',
    label: 'Recent vendors',
    icon: 'new_releases',
    blurb: `Registered in the last ${RECENT_DAYS} days, or still onboarding — asked to verify the details they gave.`,
    ask: 'verify the details on file',
    emailSubject: 'Please confirm your vendor details with Amber',
    emailLead:
      'We are confirming the details we hold for your company. Open the link, check each section and submit — about five minutes.',
  },
  {
    key: 'legacy',
    label: 'Legacy vendors',
    icon: 'history',
    blurb: `On the books for more than ${RECENT_DAYS} days — asked to complete the requirements the new form added.`,
    ask: 'complete the new requirements',
    emailSubject: 'Please complete your vendor details for Amber',
    emailLead:
      'Our vendor requirements have changed. Open the link and complete the new sections — Udyam registration, bank verification and the updated declarations.',
  },
];

export function segmentByKey(key) {
  return SEGMENTS.find((s) => s.key === key);
}

/** Still onboarding is recent whatever the date says; no date at all is the oldest thing we know of. */
export function segmentOf(vendor, nowMs = Date.now()) {
  if (String(vendor.status || '').toUpperCase() === 'PENDING') return 'recent';
  const t = vendor.created_at ? Date.parse(vendor.created_at) : NaN;
  if (!Number.isFinite(t)) return 'legacy';
  return nowMs - t <= RECENT_DAYS * DAY ? 'recent' : 'legacy';
}

export function splitBySegment(vendors, nowMs = Date.now()) {
  const out = { recent: [], legacy: [] };
  for (const v of vendors) out[segmentOf(v, nowMs)].push(v);
  return out;
}

/** A sent link's status → <StatusPill> vocabulary + label. */
export const LINK_STATUS_PILL = {
  sent: { pill: 'scheduled', label: 'Link sent' },
  submitted: { pill: 'pending', label: 'Submitted' },
  verified: { pill: 'approved', label: 'Verified' },
  expired: { pill: 'draft', label: 'Expired' },
};

/** A batch's derived status (the server's `status`) → pill + label. `closed` names its expired links: see batchStatusPill. */
export const BATCH_STATUS_PILL = {
  pending: { pill: 'running', label: 'In progress' },
  review: { pill: 'pending', label: 'Needs review' },
  closed: { pill: 'draft', label: 'Closed' },
  completed: { pill: 'completed', label: 'Completed' },
};

/** Mail that never reached the vendor → pill + label. Anything else reads as a quiet line (DELIVERY_LABEL). */
export const DELIVERY_PILL = {
  failed: { pill: 'failed', label: 'Mail failed' },
  bounced: { pill: 'failed', label: 'Bounced' },
};

export const DELIVERY_LABEL = {
  queued: 'Mail queued',
  sent: 'Mail sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  bounced: 'Bounced',
  failed: 'Mail failed',
};

/** Did the mail carrying this vendor's link fail at the provider or bounce? */
export function isUndelivered(vendor) {
  return vendor?.delivery === 'failed' || vendor?.delivery === 'bounced';
}

export function batchCounts(batch) {
  const counts = { total: 0, sent: 0, submitted: 0, verified: 0, expired: 0, undelivered: 0 };
  for (const v of batch.vendors || []) {
    counts.total += 1;
    if (v.status in counts) counts[v.status] += 1;
    if (isUndelivered(v)) counts.undelivered += 1;
  }
  return counts;
}

/**
 * The same rules as the server's batchView: completed when every link is verified; needs review
 * while an answer waits for an admin; closed once no link is still open; otherwise in progress.
 */
export function deriveBatchStatus(batch) {
  const c = batchCounts(batch);
  if (c.total === 0) return 'pending';
  if (c.verified === c.total) return 'completed';
  if (c.submitted > 0) return 'review';
  if (c.sent === 0) return 'closed';
  return 'pending';
}

export function batchStatusPill(batch) {
  const p = BATCH_STATUS_PILL[batch?.status] || BATCH_STATUS_PILL.pending;
  if (batch?.status !== 'closed') return p;
  const expired = batch.counts?.expired ?? batchCounts(batch).expired;
  return { pill: p.pill, label: `Closed — ${Number(expired || 0).toLocaleString('en-IN')} expired` };
}

/**
 * What can be done with a batch, per vendor: remind the open links whose mail went out; resend the
 * open links whose mail failed or bounced (the same link again); review the submitted answers; send
 * a fresh link to the expired. A vendor with no address can be neither reminded nor resent, and a
 * vendor reminded less than REMIND_COOLDOWN_HOURS ago is not offered another — the server refuses it.
 */
export const REMIND_COOLDOWN_HOURS = 12;

export function batchActions(batch, now = new Date()) {
  const out = { remind: [], resend: [], review: 0, fresh: [] };
  const cooldownMs = REMIND_COOLDOWN_HOURS * 3_600_000;
  const coolingDown = (v) => Boolean(v.reminded_at) && now.getTime() - new Date(v.reminded_at).getTime() < cooldownMs;
  for (const v of batch?.vendors || []) {
    if (v.status === 'submitted') out.review += 1;
    else if (v.status === 'expired') out.fresh.push(v.vendor_id);
    else if (v.status === 'sent' && v.email) {
      if (isUndelivered(v)) out.resend.push(v.vendor_id);
      else if (!coolingDown(v)) out.remind.push(v.vendor_id);
    }
  }
  return out;
}

const ASK_ORDER = ASKS.map((a) => a.key);

/**
 * What a fresh link for these vendors should ask: everything their last links asked, in catalogue
 * order — the whole form stands alone. `linkOf(id)` is the newest link row (with its batch's asks).
 */
export function asksForVendors(vendorIds, linkOf) {
  const wanted = new Set();
  for (const id of vendorIds) for (const k of linkOf(id)?.asks || []) wanted.add(k);
  if (wanted.has('full')) return ['full'];
  return ASK_ORDER.filter((k) => k !== 'full' && wanted.has(k));
}

/** The newest link a vendor was sent, across every batch, or null if they were never sent one. */
export function latestLinkFor(batches, vendorId) {
  const id = Number(vendorId);
  const sorted = [...batches].sort((a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
  for (const b of sorted) {
    const v = (b.vendors || []).find((x) => Number(x.vendor_id) === id);
    if (v) return { batch_id: b.id, status: v.status, at: b.created_at };
  }
  return null;
}
